import base from "./base"
import { defineStore } from 'pinia'
import { backend_api_v2 } from "@/utils/apiv2"

export const useFactoriesStore = defineStore('factories', {
    state: () => ({
        production_lines: [],
        stations: [],
        machines: [],
        triggers: [],
        action_setups: [],
        // The machine catalogue from GET /machines/models/: one entry per
        // model, with its `scopes`, `formats` and settings form name. Static
        // per release, so it is loaded once and shared instead of being
        // fetched again by every component that needs a scope.
        machine_models: [],
    }),
    getters: {
        // Setups of one kind ("mob_file", "auto_dwg"). The whole collection is
        // loaded once and filtered here, so the triggers table can name a setup
        // of any kind without loading them per kind.
        actionSetupsByKind: (state) => (kind) =>
            state.action_setups.filter((s) => s.kind == kind),
        // Where a model may be attached: "station" / "line" / "standalone".
        // A hint for the pickers, never a rule -- see machine_models.py. An
        // unknown model returns [], which is what a model added to the backend
        // catalogue after the frontend loaded looks like.
        scopesOfModel: (state) => (model) =>
            state.machine_models.find(mm => mm.name == model)?.scopes || [],

        // The machine that represents a line, mirroring
        // ProductionLine.effective_machine on the backend:
        //   1. the line's own machine_setup -- an explicit answer;
        //   2. otherwise the machine of its only machine station;
        //   3. otherwise null.
        // Recording a line's machine on one of its stations is a supported
        // configuration, so never read `line.machine_setup` directly.
        effectiveMachine() {
            return (line) => {
                if (!line) return null
                if (line.machine_setup)
                    return this.machines.find(m => m.uuid == line.machine_setup) || null
                const uuids = this.getPLStations(line)
                    .map(s => s.machine_setup)
                    .filter(Boolean)
                if (uuids.length != 1) return null
                return this.machines.find(m => m.uuid == uuids[0]) || null
            }
        },

        // True when effectiveMachine answered through a station. The manager
        // view renders an inferred machine differently, so that the two
        // placements stay distinguishable without opening the form.
        machineIsInferred() {
            return (line) =>
                !line?.machine_setup && !!this.effectiveMachine(line)
        },

        // Both halves of "is this setup used": a line machine has no stations
        // and is not orphaned.
        stationsOfMachine: (state) => (uuid) =>
            state.stations.filter(s => s.machine_setup == uuid),
        linesOfMachine: (state) => (uuid) =>
            state.production_lines.filter(l => l.machine_setup == uuid),

        getPLStations: (state) => (pl) => {
            // if pl is string, it's an uuid, pick the pl from state, otherwise it's an object
            if(!pl) return []
            if (typeof pl === "string") {
                pl = state.production_lines.find(p => p.uuid === pl)
            }
            if (!pl) return []
            return pl.stations.map(s_uuid => state.stations.find(s => s.uuid === s_uuid)).filter(s => s !== undefined)
        },
    },
    actions: {
        // Production Lines
        setProductionLines: base.actions.setter("production_lines"),
        createProductionLine: base.actions.creator("production_lines"),
        updateProductionLine: base.actions.updater("production_lines"),
        removeProductionLine: base.actions.remover("production_lines"),

        loadProductionLines: base.actions.loader("factories/production_lines/", "ProductionLines"),
        postProductionLine: base.actions.poster("factories/production_lines/", "ProductionLine"),
        putProductionLine: base.actions.putter("factories/production_lines/", "ProductionLine"),
        deleteProductionLine: base.actions.deleter("factories/production_lines/", "ProductionLine"),


        // Production 
        setStations: base.actions.setter("stations"),
        createStation: base.actions.creator("stations"),
        updateStation: base.actions.updater("stations"),
        removeStation: base.actions.remover("stations"),

        loadStations: base.actions.loader("factories/stations/", "Stations"),
        postStation: base.actions.poster("factories/stations/", "Station"),
        putStation: base.actions.putter("factories/stations/", "Station"),
        deleteStation: base.actions.deleter("factories/stations/", "Station"),


        // Machines
        setMachines: base.actions.setter("machines"),
        createMachine: base.actions.creator("machines"),
        updateMachine: base.actions.updater("machines"),
        removeMachine: base.actions.remover("machines"),

        loadMachines: base.actions.loader("factories/machines/", "Machines"),

        // Not a paginated collection: /machines/models/ answers with the
        // catalogue list as-is.
        setMachineModels: base.actions.setter("machine_models"),
        loadMachineModels() {
            if (this.machine_models.length) return Promise.resolve(this.machine_models)
            return backend_api_v2
                .get("/machines/models/")
                .then(({ data }) => {
                    this.setMachineModels(data)
                    return data
                })
                .catch(e => {
                    console.log(`Cant get MachineModels: ${e}`)
                    return []
                })
        },
        postMachine: base.actions.poster("factories/machines/", "Machine"),
        putMachine: base.actions.putter("factories/machines/", "Machine"),
        deleteMachine: base.actions.deleter("factories/machines/", "Machine"),


        // Action Triggers
        setTriggers: base.actions.setter("triggers"),
        createTrigger: base.actions.creator("triggers"),
        updateTrigger: base.actions.updater("triggers"),
        removeTrigger: base.actions.remover("triggers"),

        loadTriggers: base.actions.loader("factories/triggers/", "Triggers"),
        postTrigger: base.actions.poster("factories/triggers/", "Trigger"),
        putTrigger: base.actions.putter("factories/triggers/", "Trigger"),
        deleteTrigger: base.actions.deleter("factories/triggers/", "Trigger"),

        // Action Setups (named configs for the trigger actions: MOB files,
        // automatic drawings, ...)
        setActionSetups: base.actions.setter("action_setups"),
        createActionSetup: base.actions.creator("action_setups"),
        updateActionSetup: base.actions.updater("action_setups"),
        removeActionSetup: base.actions.remover("action_setups"),

        loadActionSetups: base.actions.loader("factories/action_setups/", "ActionSetups"),
        postActionSetup: base.actions.poster("factories/action_setups/", "ActionSetup"),
        putActionSetup: base.actions.putter("factories/action_setups/", "ActionSetup"),
        deleteActionSetup: base.actions.deleter("factories/action_setups/", "ActionSetup"),
    },
})
